/**
 * Role- and goal-based personalization content model.
 *
 * Rules this model enforces:
 *  - Personalization only REORDERS and RECOMMENDS. It never removes a section,
 *    hides a regulatory notice, changes a claim, or gates a page.
 *  - Every option is chosen explicitly by the visitor from a visible prompt.
 *    Nothing is inferred from behavior, referrer, IP, or any sensitive input.
 *  - The selection is a coarse professional interest only. It is never a
 *    clinical credential, employment record, or health-related attribute.
 *  - All recommended destinations are ordinary public pages that are also
 *    reachable from the site navigation, so search visibility is unchanged.
 */

export type SectionKey =
  | "moments"
  | "category"
  | "lifecycle"
  | "capabilities"
  | "audiences"
  | "architecture"
  | "evidence"
  | "resources";

/** Default homepage order — used whenever no selection exists. */
export const DEFAULT_SECTION_ORDER: SectionKey[] = [
  "moments",
  "category",
  "lifecycle",
  "capabilities",
  "audiences",
  "architecture",
  "evidence",
  "resources",
];

export type Recommendation = {
  label: string;
  href: string;
  note: string;
};

export type Profile = {
  id: string;
  label: string;
  /** One plain-language sentence confirming what the visitor chose. */
  confirmation: string;
  /** Sections hoisted to the top of the homepage body. Nothing is removed. */
  priority: SectionKey[];
  /** Capabilities and pages most relevant to the selection. */
  recommendations: Recommendation[];
  /** Demo CTA wording. Every variant links to the same public demo request. */
  cta: { label: string; helper: string };
};

/* ------------------------------------------------------------------ */
/* "What would you like to improve?"                                   */
/* ------------------------------------------------------------------ */

export const goals: Profile[] = [
  {
    id: "clinical-response",
    label: "Clinical response",
    confirmation: "Showing coordination and in-event support first.",
    priority: ["moments", "capabilities", "lifecycle"],
    recommendations: [
      {
        label: "Experience the command center",
        href: "/experience",
        note: "Interactive demonstration of a synthetic event from five perspectives.",
      },
      {
        label: "Resuscitation teams",
        href: "/solutions/resuscitation-teams",
        note: "How in-room support, timing, and role clarity are designed to work.",
      },
      {
        label: "How it works",
        href: "/how-it-works",
        note: "AiMediQ™ at the point of care with datasyteAI™ coordination.",
      },
    ],
    cta: { label: "See a clinical walkthrough", helper: "A guided review of the in-event workflow." },
  },
  {
    id: "documentation",
    label: "Documentation",
    confirmation: "Showing structured capture and the event record first.",
    priority: ["capabilities", "moments", "architecture"],
    recommendations: [
      {
        label: "Documentation workflow",
        href: "/solutions/resuscitation-teams",
        note: "Structured capture during the event rather than reconstruction afterward.",
      },
      {
        label: "Interoperability and integration",
        href: "/solutions/it-security-integration",
        note: "Planned device and EHR integration approach.",
      },
      {
        label: "Resource library",
        href: "/resources",
        note: "Published material on documentation burden and event records.",
      },
    ],
    cta: { label: "See the documentation workflow", helper: "A walkthrough of the structured event record." },
  },
  {
    id: "cart-readiness",
    label: "Cart readiness",
    confirmation: "Showing readiness and supply visibility first.",
    priority: ["lifecycle", "capabilities", "moments"],
    recommendations: [
      {
        label: "Resuscitation Readiness Index",
        href: "/readiness-index",
        note: "Educational self-assessment across twelve readiness domains.",
      },
      {
        label: "Pharmacy and clinical operations",
        href: "/solutions/pharmacy-clinical-operations",
        note: "Supply, medication, and cart-status visibility.",
      },
      {
        label: "AiMediQ™ overview",
        href: "/aimediq",
        note: "The point-of-care system and its readiness design intent.",
      },
    ],
    cta: { label: "See a readiness walkthrough", helper: "A guided review of cart and supply visibility." },
  },
  {
    id: "quality-review",
    label: "Quality review",
    confirmation: "Showing post-event review and evidence work first.",
    priority: ["evidence", "capabilities", "lifecycle"],
    recommendations: [
      {
        label: "Quality and patient safety",
        href: "/solutions/quality-patient-safety",
        note: "Post-event review, debriefing, and program-level analysis.",
      },
      {
        label: "Evidence development program",
        href: "/evidence/program",
        note: "How PathFree intends to build evidence across the lifecycle.",
      },
      {
        label: "Resuscitation Operations Value Model",
        href: "/value-model",
        note: "Illustrative executive model of operational effort.",
      },
    ],
    cta: { label: "See a quality review walkthrough", helper: "A guided review of post-event analysis." },
  },
  {
    id: "training",
    label: "Training",
    confirmation: "Showing training, simulation, and improvement first.",
    priority: ["lifecycle", "moments", "resources"],
    recommendations: [
      {
        label: "Training and preparedness",
        href: "/solutions/training-preparedness",
        note: "Simulation and training-mode design intent.",
      },
      {
        label: "PathFree Institute",
        href: "/institute",
        note: "Educational and thought-leadership material.",
      },
      {
        label: "Resource library",
        href: "/resources",
        note: "Clinical education and preparedness material.",
      },
    ],
    cta: { label: "See a training walkthrough", helper: "A guided review of simulation and training use." },
  },
  {
    id: "integration-security",
    label: "Integration and security",
    confirmation: "Showing architecture, security, and trust material first.",
    priority: ["architecture", "evidence", "capabilities"],
    recommendations: [
      {
        label: "IT, security, and integration",
        href: "/solutions/it-security-integration",
        note: "Architecture, security engineering objectives, and integration plans.",
      },
      {
        label: "Trust Center",
        href: "/trust-center",
        note: "Security, privacy, quality, and regulatory status in one place.",
      },
      {
        label: "Responsible Clinical AI",
        href: "/responsible-ai",
        note: "Current and planned practices for clinical AI governance.",
      },
    ],
    cta: { label: "Request a technical review", helper: "A session focused on architecture and security." },
  },
  {
    id: "enterprise-standardization",
    label: "Enterprise standardization",
    confirmation: "Showing enterprise scope and category context first.",
    priority: ["category", "architecture", "evidence"],
    recommendations: [
      {
        label: "Enterprise deployment",
        href: "/enterprise-deployment",
        note: "The seven-stage implementation framework and responsibilities.",
      },
      {
        label: "Hospitals and health systems",
        href: "/solutions/hospitals-health-systems",
        note: "Multi-site standardization and governance intent.",
      },
      {
        label: "Why PathFree",
        href: "/why-pathfree",
        note: "Capability comparison across solution categories.",
      },
    ],
    cta: { label: "Request an enterprise briefing", helper: "A session focused on multi-site rollout." },
  },
];

/* ------------------------------------------------------------------ */
/* "What is your role?"                                                */
/* ------------------------------------------------------------------ */

export const roles: Profile[] = [
  {
    id: "clinical-leader",
    label: "Clinical leader",
    confirmation: "Showing clinical workflow and program material first.",
    priority: ["moments", "lifecycle", "capabilities"],
    recommendations: [
      { label: "Clinical approach", href: "/evidence/clinical-approach", note: "Design principles and clinical reasoning." },
      { label: "Resuscitation teams", href: "/solutions/resuscitation-teams", note: "In-event support for the people in the room." },
      { label: "Readiness Index", href: "/readiness-index", note: "Educational assessment of program readiness." },
    ],
    cta: { label: "See a clinical walkthrough", helper: "A guided review of the in-event workflow." },
  },
  {
    id: "resuscitation-team",
    label: "Resuscitation team",
    confirmation: "Showing in-room coordination first.",
    priority: ["moments", "capabilities", "lifecycle"],
    recommendations: [
      { label: "Experience the command center", href: "/experience", note: "Explore a synthetic event, stage by stage." },
      { label: "How it works", href: "/how-it-works", note: "AiMediQ™ and datasyteAI™ during an event." },
      { label: "Training and preparedness", href: "/solutions/training-preparedness", note: "Simulation and practice design intent." },
    ],
    cta: { label: "See the team workflow", helper: "A walkthrough of roles, timing, and capture." },
  },
  {
    id: "quality-safety",
    label: "Quality and safety",
    confirmation: "Showing review, analytics, and evidence first.",
    priority: ["evidence", "capabilities", "lifecycle"],
    recommendations: [
      { label: "Quality and patient safety", href: "/solutions/quality-patient-safety", note: "Review, debriefing, and program analysis." },
      { label: "Evidence development program", href: "/evidence/program", note: "Planned studies and current status." },
      { label: "Regulatory and quality", href: "/evidence/regulatory-quality", note: "Development-phase quality practices." },
    ],
    cta: { label: "See a quality review walkthrough", helper: "A guided review of post-event analysis." },
  },
  {
    id: "pharmacy-operations",
    label: "Pharmacy or operations",
    confirmation: "Showing supply, medication, and readiness first.",
    priority: ["lifecycle", "capabilities", "architecture"],
    recommendations: [
      { label: "Pharmacy and clinical operations", href: "/solutions/pharmacy-clinical-operations", note: "Medication and supply visibility." },
      { label: "Readiness Index", href: "/readiness-index", note: "Assessment covering supply and device readiness." },
      { label: "Value Model", href: "/value-model", note: "Illustrative model of operational effort." },
    ],
    cta: { label: "See an operations walkthrough", helper: "A guided review of readiness and supply visibility." },
  },
  {
    id: "it-security",
    label: "IT or security",
    confirmation: "Showing architecture, security, and integration first.",
    priority: ["architecture", "evidence", "capabilities"],
    recommendations: [
      { label: "IT, security, and integration", href: "/solutions/it-security-integration", note: "Architecture and integration approach." },
      { label: "Trust Center", href: "/trust-center", note: "Security, privacy, and documentation requests." },
      { label: "Responsible Clinical AI", href: "/responsible-ai", note: "Governance, oversight, and model practices." },
    ],
    cta: { label: "Request a technical review", helper: "A session focused on architecture and security." },
  },
  {
    id: "executive-leadership",
    label: "Executive leadership",
    confirmation: "Showing category context and enterprise scope first.",
    priority: ["category", "evidence", "architecture"],
    recommendations: [
      { label: "Value Model", href: "/value-model", note: "Illustrative executive model, clearly labeled." },
      { label: "Enterprise deployment", href: "/enterprise-deployment", note: "Implementation framework and responsibilities." },
      { label: "Why PathFree", href: "/why-pathfree", note: "Differentiation without superiority claims." },
    ],
    cta: { label: "Request an executive briefing", helper: "A session focused on strategy and scope." },
  },
  {
    id: "strategic-partner",
    label: "Strategic partner",
    confirmation: "Showing partnership and company material first.",
    priority: ["category", "architecture", "resources"],
    recommendations: [
      { label: "Strategic partnerships", href: "/partnerships", note: "Categories, process, and inquiry form." },
      { label: "Regulatory status", href: "/regulatory-status", note: "Current pre-market development phase." },
      { label: "Newsroom", href: "/newsroom", note: "Verified announcements and press kit." },
    ],
    cta: { label: "Explore a strategic partnership", helper: "Start a partnership conversation." },
  },
];

export type PersonalizationMode = "goal" | "role";

export const findProfile = (mode: PersonalizationMode, id: string): Profile | undefined =>
  (mode === "goal" ? goals : roles).find((p) => p.id === id);

/** Priority sections first, remaining default sections after. Nothing is dropped. */
export const orderSections = (profile?: Profile): SectionKey[] => {
  if (!profile) return DEFAULT_SECTION_ORDER;
  const priority = profile.priority.filter((k) => DEFAULT_SECTION_ORDER.includes(k));
  return [...priority, ...DEFAULT_SECTION_ORDER.filter((k) => !priority.includes(k))];
};
